import { ConfigForData } from '@droplet-tech-code/core-common-types';
import {
  SystemConfigAddFerryTermCategoryRequest,
  SystemConfigClientGetTermsConfigResponse,
  SystemConfigClientUpdateCompanyTermsConfigRequest,
  SystemConfigClientUpdatePrivacyTermsConfigRequest,
  SystemConfigGetFerryConfigResponse,
} from '@naus-code/naus-admin-types';
import { useCallback } from 'react';

import { useFormModal } from '~/components/Modals/FormModal';
import { systemConfigApi } from '~/screens/Configs/System/systemConfigs.api';
import { translate } from '~/utils/translation.utils';

export const useUpdateCompanyTermsConfig = () => {
  const { open: openForm, close: closeForm } = useFormModal();

  const useFormHookHandler = useCallback(() => {
    const { data, isLoading } =
      systemConfigApi.useGetUpdateCompanyTermsConfigFormConfigQuery();
    return { data, isLoading };
  }, []);

  const onConfirm = async ({
    action,
    value,
    check,
  }: {
    action: (
      req: SystemConfigClientUpdateCompanyTermsConfigRequest,
    ) => SystemConfigClientGetTermsConfigResponse;
    value: SystemConfigClientUpdateCompanyTermsConfigRequest['companyTerms'];
    check?: string;
  }) => {
    return await action({
      companyTerms: value,
      check: check || '',
    });
  };

  return {
    open: () => {
      openForm({
        modalTitle: translate('systemConfig.updateCompanyTerms'),
        useFormHookHandler,
        onConfirm,
        actionHook: systemConfigApi.useUpdatePrivacyTermsConfigMutation,
      });
    },
    close: () => {
      closeForm();
    },
  };
};

export const useUpdatePrivacyTermsConfig = () => {
  const { open: openForm, close: closeForm } = useFormModal();

  const useFormHookHandler = useCallback(() => {
    const { data, isLoading } =
      systemConfigApi.useGetUpdatePrivacyTermsConfigFormConfigQuery();
    return { data, isLoading };
  }, []);

  const onConfirm = async ({
    action,
    value,
    check,
  }: {
    action: (
      req: SystemConfigClientUpdatePrivacyTermsConfigRequest,
    ) => SystemConfigClientGetTermsConfigResponse;
    value: SystemConfigClientUpdatePrivacyTermsConfigRequest['privacyPolicy'];
    check?: string;
  }) => {
    return await action({
      privacyPolicy: value,
      check: check || '',
    });
  };

  return {
    open: () => {
      openForm({
        modalTitle: translate('systemConfig.updatePrivacyTerms'),
        useFormHookHandler,
        onConfirm,
        actionHook: systemConfigApi.useUpdatePrivacyTermsConfigMutation,
      });
    },
    close: () => {
      closeForm();
    },
  };
};

export const useAddTermCategory = ({ check }: { check?: string }) => {
  const { open: openForm, close: closeForm } = useFormModal();

  const onConfirm = async ({
    action,
    value,
    check,
  }: {
    action: (
      req: SystemConfigAddFerryTermCategoryRequest,
    ) => SystemConfigGetFerryConfigResponse;
    value: SystemConfigAddFerryTermCategoryRequest['category'];
    check?: string;
  }) => {
    return await action({
      category: value,
      check: check || '',
    });
  };

  return {
    open: (supportedLangs: string[]) => {
      const config: ConfigForData = [
        {
          items: [
            {
              type: 'text',
              label: translate('systemConfig.name'),
              id: 'name',
            },
            {
              type: 'lang-text',
              label: translate('systemConfig.displayName'),
              list: supportedLangs.map((langName) => {
                return { value: langName };
              }),
              id: 'displayName',
            },
          ],
        },
      ];
      openForm({
        modalTitle: translate('systemConfig.addTermCategory'),
        useFormHookHandler: () => {
          return { data: { config, check }, isLoading: false };
        },
        onConfirm,
        actionHook: systemConfigApi.useAddTermCategoryMutation,
      });
    },
    close: () => {
      closeForm();
    },
  };
};
